import { FC, useState } from 'react'

import { RenderIf } from 'components/RenderIf'
import { IconFull } from 'components/IconsContainers/styled'
import { Sidebar } from 'featureSliced/widgets/Sidebar'
import { VideoBackground } from 'featureSliced/features/VideoBackground'
import { ButtonsCC } from 'featureSliced/widgets/ButtonsMainScreen/ButtonsCC'
import { ButtonsQRM } from 'featureSliced/widgets/ButtonsMainScreen/ButtonsQRM'
import { QRMInstructions } from 'featureSliced/widgets/ButtonsMainScreen/QRMInstructions'
import { LanguageSelector } from 'featureSliced/widgets/LanguageSelector'

import { useMySelector } from 'featureSliced/shared/hooks/useMySelector'
import { useTranslations } from 'featureSliced/shared/hooks/useTranslations'
import { useOpenNotificationsModal } from 'featureSliced/shared/hooks/openModals/useOpenNotificationsModal'
import { useOpenAppGuideModal } from 'featureSliced/shared/hooks/openAppGuideModal'

import { ReactComponent as Bell } from 'assets/cover/bellFilled.svg'
import { ReactComponent as Burger } from 'assets/cover/burgerTwoColor.svg'
import { ReactComponent as InfoIcon } from 'assets/cover/infoIconFilled.svg'
import clickAndCollectLogo from 'assets/prompt/clickAndCollect.svg'
import qrMenuLogo from 'assets/prompt/qrMenu.svg'

import type { PageType } from 'featureSliced/padges/MainScreen'
import {
  CoverActions,
  CoverCenter,
  CoverHeader,
  CoverLogo,
  CoverNote,
  CoverScreen,
  CoverScrim,
  WebActionBadge,
  WebActionWithBadge,
} from './coverStyled'
import { WebActions, WebHeaderSpacer, WebLogo } from './styled'

type CoverWebProps = {
  openProfileModal: (startPage?: PageType) => void
  openAuthModal: () => void
  handleLoyaltyButtonClick: () => void
  userName?: string | false
  notificationsAmount: number | string
  getNotificationsAmount: () => void
}

const IS_CLICK_COLLECT = process.env.REACT_APP_IS_CLIC === '1'

/**
 * QRM-641: the desktop cover. Same content as the phone — tenant background, logo,
 * the one or two entry buttons, language — but the controls move into a thin header
 * with the burger last, and the centre block is sized for a monitor instead of
 * inheriting the phone's vw geometry (MainScreenControls uses 5vw paddings, which is
 * 72px at 1440 and put the burger nowhere near the corner).
 *
 * Deliberately still a cover: the branch and pickup-time dialogs stay on entering the
 * menu, they were not moved here.
 */
export const CoverWeb: FC<CoverWebProps> = ({
  openProfileModal,
  openAuthModal,
  handleLoyaltyButtonClick,
  userName,
  notificationsAmount,
  getNotificationsAmount,
}) => {
  const t = useTranslations()
  const openAppGuideModal = useOpenAppGuideModal()
  const openNotificationsModal = useOpenNotificationsModal(
    getNotificationsAmount
  )

  const { logoImage } = useMySelector((state) => state.app.interfaceStyles)
  const waiterCall = useMySelector(
    (state) => state.app.interfaceStyles.waiterCall
  )
  const { paymentVariant, selfService } = useMySelector(
    (state) => state.app.menu
  )

  const [isSidebarShowing, setIsSidebarShowing] = useState(false)

  const fallbackLogo = IS_CLICK_COLLECT ? clickAndCollectLogo : qrMenuLogo

  // same visibility rules the phone controls use — kept in step deliberately
  const isBellShown = IS_CLICK_COLLECT || (!selfService && waiterCall)
  const isGuideShown = !IS_CLICK_COLLECT && paymentVariant === 2

  return (
    <CoverScreen data-testid="cover-web">
      <VideoBackground />
      <CoverScrim />

      <Sidebar
        isSidebarShowing={isSidebarShowing}
        setIsSidebarShowing={setIsSidebarShowing}
        openProfileModal={openProfileModal}
        openAuthModal={openAuthModal}
        userName={userName}
      />

      <CoverHeader data-testid="cover-header">
        <WebLogo src={logoImage || fallbackLogo} alt="" />
        <WebHeaderSpacer />
        <WebActions>
          <RenderIf condition={isBellShown}>
            <WebActionWithBadge
              type="button"
              onClick={openNotificationsModal}
              aria-label={t('common.notifications')}
            >
              <IconFull fill="#fff" stroke="none">
                <Bell width="20" height="20" />
              </IconFull>
              <RenderIf condition={!!notificationsAmount}>
                <WebActionBadge>{notificationsAmount}</WebActionBadge>
              </RenderIf>
            </WebActionWithBadge>
          </RenderIf>

          {/* no label: the guide is a wordless animation and no i18n key describes
              it yet — one is on the list to request, same as the burger's */}
          <RenderIf condition={isGuideShown}>
            <WebActionWithBadge type="button" onClick={openAppGuideModal}>
              <IconFull fill="#fff" stroke="none">
                <InfoIcon width="20" height="20" />
              </IconFull>
            </WebActionWithBadge>
          </RenderIf>

          {/* the burger is always the last icon on the right */}
          <WebActionWithBadge
            type="button"
            onClick={() => setIsSidebarShowing(true)}
            aria-label={t('cover.menu')}
          >
            <IconFull fill="#fff" stroke="none">
              <Burger width="20" height="20" />
            </IconFull>
          </WebActionWithBadge>
        </WebActions>
      </CoverHeader>

      <CoverCenter>
        <RenderIf condition={!!logoImage}>
          <CoverLogo src={logoImage} alt="" />
        </RenderIf>

        <CoverActions>
          <ButtonsQRM />
          <ButtonsCC handleLoyaltyButtonClick={handleLoyaltyButtonClick} />
        </CoverActions>

        {/* the wrapper caps the admin's free text, but an always-rendered empty div
            would still eat a flex gap — so it mirrors QRMInstructions' own condition */}
        <RenderIf condition={!IS_CLICK_COLLECT && selfService === 1}>
          <CoverNote>
            <QRMInstructions />
          </CoverNote>
        </RenderIf>

        <LanguageSelector />
      </CoverCenter>
    </CoverScreen>
  )
}
